import { useState, useEffect, useCallback } from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useBooking } from '../../../../context/bookingContext';
import useInternalBooking from '../../../../hooks/useInternalBooking';

// Maps a present booking returned by the backend into the flat customer data shape
// used by the booking context / CustomerData form.
const mapBookingToCustomerData = booking => ({
  firstName: booking.firstName || '',
  lastName: booking.lastName || '',
  mail: booking.mail || '',
  phone: booking.phone || '',
  companyName: booking.companyName || '',
  street: booking.contactData?.street || '',
  postcode:
    booking.contactData?.postcode !== undefined && booking.contactData?.postcode !== null
      ? String(booking.contactData.postcode)
      : '',
  city: booking.contactData?.city || '',
  birthdate: booking.contactData?.birthdate
    ? dayjs(booking.contactData.birthdate).format('YYYY-MM-DD')
    : '',
});

// Builds the dropdown label. The list spans all entertainments of the branch and the whole day, so
// entertainment and check-in time are needed to tell entries apart and to keep it readable.
const buildOptionLabel = booking => {
  const name = [booking.firstName, booking.lastName].filter(Boolean).join(' ').trim();
  const entertainmentName = booking.entertainmentId?.entertainmentName || '';
  const checkInTime = booking.presentSince ? dayjs(booking.presentSince).format('HH:mm') : '';
  return [name || 'Ohne Namen', entertainmentName, checkInTime].filter(Boolean).join(' · ');
};

// Dropdown shown at the top of the create booking modal that lets staff copy customer data from a
// customer who checked in at the selected branch today into the new booking. Deliberately not
// limited to the selected entertainment or to currently running bookings: guests regularly book a
// second entertainment right after finishing the first one.
export default function CopyCustomerDataSelect() {
  const { selectedBranch, applyCustomerData } = useBooking();
  const { getPresentCustomersForBranch } = useInternalBooking();
  const [presentCustomers, setPresentCustomers] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!selectedBranch) {
      setPresentCustomers([]);
      setSelectedBookingId('');
      return undefined;
    }

    const fetchPresentCustomers = async () => {
      try {
        const customers = await getPresentCustomersForBranch(selectedBranch);
        if (!cancelled) {
          setPresentCustomers(Array.isArray(customers) ? customers : []);
          setSelectedBookingId('');
        }
      } catch (error) {
        if (!cancelled) {
          setPresentCustomers([]);
        }
        console.error('Error fetching present customers:', error);
      }
    };

    fetchPresentCustomers();
    return () => {
      cancelled = true;
    };
  }, [selectedBranch]);

  const handleSelect = useCallback(
    event => {
      const bookingId = event.target.value;
      setSelectedBookingId(bookingId);
      const booking = presentCustomers.find(entry => entry._id === bookingId);
      if (!booking) return;
      applyCustomerData(mapBookingToCustomerData(booking));
      toast.success(`Kundendaten von ${booking.firstName} ${booking.lastName} übernommen`);
    },
    [presentCustomers, applyCustomerData]
  );

  // Nothing to suggest: no branch selected yet or nobody checked in today
  if (!selectedBranch || presentCustomers.length === 0) return null;

  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <TextField
        select
        fullWidth
        size='small'
        label='Kundendaten übernehmen von'
        value={selectedBookingId}
        onChange={handleSelect}
        InputProps={{
          startAdornment: (
            <ContentCopyIcon fontSize='small' sx={{ mr: 1, color: 'action.active' }} />
          ),
        }}
      >
        {presentCustomers.map(booking => (
          <MenuItem key={booking._id} value={booking._id}>
            {buildOptionLabel(booking)}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
